import React, {useCallback, useEffect} from 'react';

import {openRegistrationModalSignal} from '../signals/Registration.signals';
import {browserNotificationService} from '../services/BrowserNotification.service';
import {
    useRegistrationContext,
    useRegistrationStep,
    useRegistrationStatus,
    useRegistrationError,
    useRegistrationIdps,
} from '../hooks/registration';
import {useSsoPopup} from '../hooks/useSsoPopup';
import {RegistrationError} from '../types';
import {config} from '../config';
import {RegistrationView} from '../views/Registration/Registration.view';

export const Registration: React.FC = () => {
    const registration = useRegistrationContext();

    const step = useRegistrationStep();
    const error = useRegistrationError();
    const idps = useRegistrationIdps();

    const isOpen = step !== null;
    const isLoading = useRegistrationStatus() === 'registering';

    const setPostRegistrationStep = useCallback(() => {
        if (browserNotificationService.getPermissionState() === 'default') {
            registration.setStep('notifications');
        } else {
            registration.setStep('success');
        }
    }, [registration]);

    const onSsoMessage = useCallback(
        async (message: Parameters<typeof registration.handleSsoResult>[0]) => {
            try {
                await registration.handleSsoResult(message);
                if (registration.getStatus() === 'registered') {
                    setPostRegistrationStep();
                }
            } catch {
                registration.setError(RegistrationError.UnexpectedError);
                registration.setStep('register');
            }
        },
        [registration, setPostRegistrationStep],
    );

    const onPopupClosed = useCallback(() => {
        registration.setStep('register');
    }, [registration]);

    const {openPopup, closePopup} = useSsoPopup({onSsoMessage, onPopupClosed});

    useEffect(
        () =>
            openRegistrationModalSignal.add(() => {
                registration.reset();
                registration.setStep('register');
            }),
        [registration],
    );

    useEffect(() => {
        const deviceAlias = config.get('deviceAlias');
        if (!deviceAlias) {
            return;
        }
        void registration
            .register({deviceAlias, exchangeToken: true})
            .then(result => {
                if (result.type !== 'success') {
                    registration.setStep('expired');
                }
            })
            .catch(() => {
                registration.setStep('expired');
            });
    }, [registration]);

    const handleClose = useCallback(() => {
        registration.reset();
        closePopup();
    }, [registration, closePopup]);

    const handleRegisterSubmit = useCallback(
        async (deviceAlias: string) => {
            try {
                const result = await registration.register({deviceAlias});
                switch (result.type) {
                    case 'redirect':
                        registration.setIdps([
                            {
                                redirectUrl: result.redirectUrl,
                                disablePopupFlow: result.disablePopupFlow,
                                ...result.redirectIdp,
                            },
                        ]);
                        registration.setStep('select-idp');
                        break;
                    case 'success':
                        setPostRegistrationStep();
                        break;
                }
            } catch {
                registration.setError(RegistrationError.UnexpectedError);
                registration.setStep('register');
            }
        },
        [registration, setPostRegistrationStep],
    );

    const handleReRegister = useCallback(() => {
        const deviceAlias = registration.getAlias();
        if (deviceAlias) {
            void handleRegisterSubmit(deviceAlias);
        }
    }, [registration, handleRegisterSubmit]);

    const handleIdp = useCallback(
        async (
            chosenIdp: string,
            redirectUrl?: string,
            disablePopupFlow?: boolean,
        ) => {
            if (redirectUrl) {
                openPopup(redirectUrl, disablePopupFlow);
                return;
            }

            try {
                const result = await registration.register({chosenIdp});

                switch (result.type) {
                    case 'redirect':
                        openPopup(result.redirectUrl, result.disablePopupFlow);
                        break;
                    case 'success':
                        setPostRegistrationStep();
                        break;
                }
            } catch {
                registration.setError(RegistrationError.UnexpectedError);
                registration.setStep('register');
            }
        },
        [openPopup, registration, setPostRegistrationStep],
    );

    const handleNotificationsSubmit = useCallback(async () => {
        await browserNotificationService.requestPermission();
        registration.setStep('success');
    }, [registration]);

    const handleNotificationsSkip = useCallback(async () => {
        registration.setStep('success');
    }, [registration]);

    return (
        <RegistrationView
            error={error}
            isOpen={isOpen}
            isLoading={isLoading}
            step={step}
            idps={idps}
            handleRegisterSubmit={handleRegisterSubmit}
            handleIdp={handleIdp}
            handleNotificationsSubmit={handleNotificationsSubmit}
            handleNotificationsSkip={handleNotificationsSkip}
            handleReRegister={handleReRegister}
            handleClose={handleClose}
        />
    );
};
